import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";
import { createHashRouter, RouterProvider, useNavigate } from "react-router-dom";
import { folders as apiFolders } from "./api";
import type { Folder, LoginData } from "./types";
import { MailShell } from "./MailShell";
import { Compose } from "./components/Compose";
import { SettingsApp } from "./settings/SettingsApp";

/* Shared mail state: folder list + session, available to all routes. */
interface MailContextValue {
    loginData: LoginData;
    folderList: Folder[];
    setFolderList: React.Dispatch<React.SetStateAction<Folder[]>>;
    refreshFolders: () => Promise<void>;
    onLogout: () => void;
}

const MailContext = createContext<MailContextValue | null>(null);

export function useMail(): MailContextValue {
    const ctx = useContext(MailContext);
    if (!ctx) throw new Error("useMail must be used inside <MailApp>");
    return ctx;
}

const LAST_FOLDER_KEY = "df-last-folder";

export function lastFolder(): string | null {
    return localStorage.getItem(LAST_FOLDER_KEY);
}

export function rememberFolder(folder: string): void {
    localStorage.setItem(LAST_FOLDER_KEY, folder);
}

export function MailApp({ loginData, onLogout }: { loginData: LoginData; onLogout: () => void }) {
    const [folderList, setFolderList] = useState<Folder[]>([]);

    const refreshFolders = useCallback(async () => {
        try {
            const result = await apiFolders();
            setFolderList(result["@Collection"]);
        } catch {
            /* silent — polling retries */
        }
    }, []);

    useEffect(() => {
        refreshFolders();
    }, [refreshFolders]);

    /* ---- auto-logout (minutes of inactivity; 0 = never) ---- */
    const autoLogoutMinutes = loginData.AutoLogout ?? 0;
    useEffect(() => {
        if (!autoLogoutMinutes) return;
        let timer: ReturnType<typeof setTimeout>;
        const arm = () => {
            clearTimeout(timer);
            timer = setTimeout(onLogout, autoLogoutMinutes * 60 * 1000);
        };
        arm();
        // Any user interaction resets the idle clock.
        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
        for (const ev of events) window.addEventListener(ev, arm, { passive: true });
        return () => {
            clearTimeout(timer);
            for (const ev of events) window.removeEventListener(ev, arm);
        };
    }, [autoLogoutMinutes, onLogout]);

    const value = useMemo(
        () => ({ loginData, folderList, setFolderList, refreshFolders, onLogout }),
        [loginData, folderList, refreshFolders, onLogout]
    );

    /* Data router (not the declarative <HashRouter>) — required by useBlocker
     * (compose dirty guard). RouterProvider renders inside the provider, so
     * routes keep access to useMail(). */
    const router = useMemo(
        () =>
            createHashRouter([
                { path: "/", element: <DefaultRedirect /> },
                { path: "/compose", element: <Compose /> },
                { path: "/settings", element: <SettingsApp /> },
                { path: "/:folder", element: <MailShell /> },
                { path: "/:folder/:uid", element: <MailShell /> },
            ]),
        []
    );

    return (
        <MailContext.Provider value={value}>
            <RouterProvider router={router} />
        </MailContext.Provider>
    );
}

/* "/" → the last used folder (or Inbox) once folders are known. */
function DefaultRedirect() {
    const { folderList } = useMail();
    const navigate = useNavigate();

    useEffect(() => {
        if (!folderList.length) return;
        const last = lastFolder();
        const target =
            folderList.find((f) => f.fullName === last) ??
            folderList.find((f) => (f.role ?? "").toLowerCase() === "inbox") ??
            folderList[0];
        navigate(`/${encodeURIComponent(target.fullName)}`, { replace: true });
    }, [folderList, navigate]);

    return null;
}

export function FolderLink({ folder, children }: { folder: string; children: ReactNode }) {
    const navigate = useNavigate();
    return (
        <span
            className="cursor-pointer text-orange-400 underline underline-offset-2 hover:text-orange-300"
            onClick={() => {
                rememberFolder(folder);
                navigate(`/${encodeURIComponent(folder)}`);
            }}
        >
            {children}
        </span>
    );
}
